import { ref } from 'vue';
import { Status } from '@/constants/api.constants';
import { getAnonymousCartId } from '@/utils/domainUtils';
import type { Cart, CartsApiResponse } from '@/types/cart-types';
import type { Instance } from '@/services/miele-api-service';

export default function useCart() {
    const cartContents = ref<Cart[] | null>(null);
    const cartStatus = ref(Status.IDLE);
    const cartCount = ref(0);

    const handleCartsResponse = (cartData: CartsApiResponse) => {
        if (!cartData?.carts) {
            cartContents.value = [];
        } else {
            const cartDataCleaned = cartData.carts.filter((element) => Object.keys(element).length !== 0);
            cartContents.value = cartDataCleaned;
        }
    };

    async function getCart(service: Instance, site_id: string, hasSession: boolean) {
        cartStatus.value = Status.RUNNING;

        if (hasSession) {
            await getLoggedInCart(service);
        } else {
            await getAnonymousCart(service, site_id);
        }
    }

    async function getCartCount(service: Instance, siteId: string, hasSession: boolean) {
        if (hasSession) {
            await service
                .getUserCartsCount()
                .then((cartsCount) => {
                    cartCount.value = cartsCount.count;
                })
                .catch((error) => {
                    console.error('Unable to fetch cart count', error);
                    cartCount.value = 0;
                });
        } else {
            const cartUUID = await getAnonymousCartId(siteId);
            if (cartUUID !== null) {
                await service
                    .getAnonymousCartsCount(cartUUID)
                    .then((cartsCount) => {
                        cartCount.value = cartsCount.count;
                    })
                    .catch((error) => {
                        console.error('Unable to fetch cart count', error);
                        cartCount.value = 0;
                    });
            }
        }
    }

    async function getLoggedInCart(service: Instance) {
        cartStatus.value = Status.RUNNING;

        await service
            .getUserCarts()
            .then((data) => {
                handleCartsResponse(data);
                cartStatus.value = Status.SUCCESS;
            })
            .catch((error) => {
                console.error('Unable to fetch cart contents', error);
                cartContents.value = null;
                cartStatus.value = Status.ERROR;
            });
    }

    async function getAnonymousCart(service: Instance, siteId: string) {
        cartStatus.value = Status.RUNNING;

        const cartUUID = await getAnonymousCartId(siteId);
        if (cartUUID === null) {
            cartStatus.value = Status.SUCCESS;
            return;
        }

        await service
            .getAnonymousCarts(cartUUID)
            .then((data) => {
                handleCartsResponse(data);
                cartStatus.value = Status.SUCCESS;
            })
            .catch((error) => {
                console.error('Unable to fetch cart contents', error);
                cartContents.value = null;
                cartStatus.value = Status.ERROR;
            });
    }

    return {
        cartContents,
        cartCount,
        cartStatus,
        getCart,
        getCartCount,
    };
}
